import type { ReactElement } from 'react';
import { useParams } from 'next/navigation';
import { GlobalNavigationDirectionText, GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { ViewerOptionKey, ViewerTheme } from '@diritto/common/viewer/constants/viewer.constant';
import { Typography } from '@diritto/common/style/typography';
import { ButtonSize, ButtonType } from 'constants/button';
import { GNBHeight, HeaderNavBarHeight } from 'constants/layout';
import { ViewerThemeColorSet } from 'constants/viewer';
import { useViewerStyleState } from 'contexts/ViewerStyleProvider';
import { useViewPort } from 'ui-hooks/useViewPort';
import {
  BlockAccessCertificateLinkButton,
  BlockAccessContainer,
  BlockAccessLinkButton,
  BlockAccessHomeLinkButton,
  BlockAccessParagraph,
  BlockAccessSignLinkButton,
  BlockAccessTitle,
} from 'ui-components/common/BlockAccess';
import { AccessBlockCode } from 'utils/checkAccessBlock';

interface BlockContentsProps {
  accessBlockCode: AccessBlockCode;
  className?: string;
}

type BlockParam = {
  title?: string;
  message: string;
  component: () => ReactElement;
};

export const BlockContents = ({ accessBlockCode, className }: BlockContentsProps) => {
  const { viewerOptions } = useViewerStyleState();
  const theme = viewerOptions?.[ViewerOptionKey.Theme] ?? ViewerTheme.White;

  const paramsFromRoute = useParams<{ contentId?: string }>();
  const contentId = paramsFromRoute?.contentId ? Number(paramsFromRoute.contentId) : undefined;

  const params: Record<AccessBlockCode, BlockParam> = {
    [AccessBlockCode.AuthorizationOver15YearsOldRequired]: {
      message: '해당 콘텐츠는 로그인한\n만 15세 이상의 사용자만 볼 수 있어요.',
      component: () => <BlockAccessSignLinkButton label="로그인" />,
    },
    [AccessBlockCode.AuthorizationAdultRequired]: {
      title: '로그인을 해주세요.',
      message: '해당 회차는 로그인한\n만 19세 이상의 사용자만 볼 수 있어요.',
      component: () => (
        <BlockAccessSignLinkButton buttonTypography={Typography.XXXSmall} size={ButtonSize.Small} label="로그인" />
      ),
    },
    [AccessBlockCode.AgeVerifiedRequired]: {
      message: '해당 콘텐츠를 이용하려면\n본인인증이 필요해요.',
      component: () => <BlockAccessCertificateLinkButton label="휴대폰 번호로 본인인증하기" />,
    },
    [AccessBlockCode.Over15YearsOldRequired]: {
      message: '해당 콘텐츠는\n만 15세 이상의 사용자만 볼 수 있어요.',
      component: () => <BlockAccessHomeLinkButton label={GlobalNavigationDirectionText[GlobalNavigationMenu.Home]} />,
    },
    [AccessBlockCode.AdultRequired]: {
      message: '해당 콘텐츠는\n만 19세 이상의 사용자만 볼 수 있어요.',
      component: () => <BlockAccessHomeLinkButton label={GlobalNavigationDirectionText[GlobalNavigationMenu.Home]} />,
    },
    [AccessBlockCode.HiddenContents]: {
      message: '회차 내용을 볼 수 없는 작품이에요.',
      component: () =>
        contentId ? (
          <BlockAccessLinkButton
            href={`/contents/${contentId}`}
            size={ButtonSize.Small}
            buttonType={ButtonType.SecondaryNeutral}
            buttonTypography={Typography.XXXSmall}
            label="작품 상세로 가기"
          />
        ) : (
          <BlockAccessHomeLinkButton
            size={ButtonSize.Small}
            buttonType={ButtonType.SecondaryNeutral}
            buttonTypography={Typography.XXXSmall}
            label={GlobalNavigationDirectionText[GlobalNavigationMenu.Home]}
          />
        ),
    },
  };

  const param = params[accessBlockCode];
  const themeColor = ViewerThemeColorSet[theme].color;

  const { height: viewportHeight } = useViewPort();
  const containerMinHeight = Math.max(0, viewportHeight - HeaderNavBarHeight - GNBHeight);

  return (
    <BlockAccessContainer minHeight={containerMinHeight} className={`${className} flex flex-col items-center min-h-0`}>
      <BlockAccessTitle className="text-center mt-2" style={{ color: themeColor }}>
        {param.title}
      </BlockAccessTitle>
      <BlockAccessParagraph className="opacity-60 whitespace-pre-wrap text-center mt-2" style={{ color: themeColor }}>
        {param.message}
      </BlockAccessParagraph>
      {param.component()}
    </BlockAccessContainer>
  );
};
